import { RenderedService, ServiceItem, TabbedServiceDefinition } from '@/types/interfaces/service-item'
import { RenderedServicesType, ServiceItemCardType } from '@/types/enums/ServiceItem'
import { PriceBreakdown } from '@/types/interfaces/sku'
import { enrichServiceItem } from '@/app/store/PriceUtility'

function minOptionAmount(values: Array<number | null | undefined>): number | undefined {
    const amounts = values.filter((value): value is number => typeof value === 'number')
    return amounts.length ? Math.min(...amounts) : undefined
}

/** Store 2.0 search display only — not used by shared enrichPricing. */
function resolveSearchPriceAmounts(price: ServiceItem['price'] | undefined) {
    const options = price?.options ?? []

    return {
        cashPrice: price?.cash?.price ?? minOptionAmount(options.map((option) => option.cash)),
        insurancePrice: price?.insurance?.price ?? minOptionAmount(options.map((option) => option.insurance)),
        options,
    }
}

/** Lowest cash, insurance, or typical_copay from search API price breakdowns. */
export function getLowestSearchPriceCents(price: ServiceItem['price'] | undefined): number | undefined {
    const { cashPrice, insurancePrice, options } = resolveSearchPriceAmounts(price)
    const typicalCopay = options.find((option) => option.typical_copay != null)?.typical_copay ?? null
    const amounts = [cashPrice, insurancePrice, typicalCopay].filter(
        (amount): amount is number => typeof amount === 'number',
    )

    return amounts.length ? Math.min(...amounts) : undefined
}

export function enrichPricing(serviceItems: ServiceItem[] | undefined | null, authenticated: boolean) {
    if (!serviceItems?.length) return

    serviceItems.forEach((serviceItem) => {
        const cashPrice = serviceItem.price?.cash?.price
        const insurancePrice = serviceItem.price?.insurance?.price

        // helper to wrap a raw number in the required object shape
        const asBreakdown = (price?: number): PriceBreakdown | null =>
            typeof price === 'number' ? { price, primary: false } : null

        enrichServiceItem(
            serviceItem,
            {
                cash: asBreakdown(cashPrice),
                insurance: asBreakdown(insurancePrice),
                options: serviceItem.price?.options ?? [],
            },
            authenticated,
        )
    })
}

/**
 * Enriches pricing for a RenderedService. Handles both shapes:
 * - Old tabbedCardSlider: top-level `services` + `tabs` with serviceIds
 * - New tabbedCardSlider: `tabbedServices` where each tab has its own `services` array
 * - featuredCategoryRow: nested `cards` each with `services`
 * - shelfGroup: nested `shelves` each with `services`
 */
export function enrichRenderedServicePricing(rs: RenderedService, authenticated: boolean) {
    if ('tabbedServices' in rs && Array.isArray(rs.tabbedServices)) {
        rs.tabbedServices.forEach((tab: TabbedServiceDefinition) => {
            if (tab.services?.length) {
                enrichPricing(tab.services, authenticated)
            }
        })
    }
    const cards = (rs as RenderedService & { cards?: { services?: ServiceItem[] }[] }).cards
    if (cards?.length) {
        for (const card of cards) {
            if (card.services?.length) {
                enrichPricing(card.services, authenticated)
            }
        }
    }
    const shelves = (rs as RenderedService & { shelves?: { services?: ServiceItem[] }[] }).shelves
    if (shelves?.length) {
        for (const shelf of shelves) {
            if (shelf.services?.length) {
                enrichPricing(shelf.services, authenticated)
            }
        }
    }
    if (rs.services?.length) {
        enrichPricing(rs.services, authenticated)
    }
}

function hasValidSku(service: ServiceItem & { sku_id?: string; sku_name?: string }): boolean {
    const skuId = service?.skuId?.toString() || (service as { sku_id?: string })?.sku_id || service?.id?.toString()
    return !!skuId
}

/**
 * Collects all ServiceItems from a RenderedService (top-level services or tabbedServices).
 */
function collectServices(rs: RenderedService): ServiceItem[] {
    const items: ServiceItem[] = []
    if (rs.services?.length) items.push(...(rs.services as ServiceItem[]))
    if ('tabbedServices' in rs && rs.tabbedServices?.length) {
        for (const tab of rs.tabbedServices) {
            if (tab.services?.length) items.push(...tab.services)
        }
    }
    return items
}

/**
 * Derives the primary ServiceItem for a category page (used for Get Started CTAs).
 * Collects services from all sections; prefers hero/categoryHeader, then tabbedSlider/carousel.
 * Returns the first service with valid skuId (backend may send type as "categoryHeader" or "category_header").
 */
export function getPrimaryServiceFromRenderedServices(services: RenderedService[]): ServiceItem | null {
    const priority: ServiceItem[] = []
    const rest: ServiceItem[] = []

    for (const rs of services) {
        const items = collectServices(rs)
        if (items.length === 0) continue

        const first = items[0] as ServiceItem
        const isHero = typeof rs.id === 'string' && rs.id.toLowerCase().includes('hero')
        const isCategoryHeader =
            rs.type === RenderedServicesType.categoryHeader ||
            rs.type === RenderedServicesType.categoryHeaderWithSearch ||
            (rs.type === RenderedServicesType.widget && rs.cardType === ServiceItemCardType.categoryHeaderWithSearch) ||
            (rs as { type?: string }).type === 'category_header' ||
            (rs as { type?: string }).type === 'category_header_with_search'

        if (isHero || isCategoryHeader) {
            priority.push(first)
        } else {
            rest.push(first)
        }
    }

    const candidates = [...priority, ...rest]
    return candidates.find(hasValidSku) ?? candidates[0] ?? null
}
